import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { IAvailability, IAvailabilityFailure, IAvailabilitySuccess, IBookingCreate, IReservationAvailability, IServiceInfo } from 'src/app/models';
import { LiteralService, UsersService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { BookingsService } from 'src/app/services';
import { hasAvailabilityDetail } from 'src/app/forms/bookingForms';

@Component({
  selector: 'app-booking-availabilities',
  templateUrl: './booking-availabilities.component.html',
  styleUrl: './booking-availabilities.component.scss',
  standalone: false
})
export class BookingAvailabilitiesComponent implements OnInit, OnChanges {

  @Input() availabilities: any;
  @Input() showedDate: string;
  @Input() userId: any;
  @Input() newBooking: IBookingCreate;
  @Input() serviceInfos: IServiceInfo[];
  @Input() loadingAvailabilities: boolean = true;
  @Input() availabilitiesSelected: any;
  @Input() allAvailabilitiesSelected: boolean;
  @Output() manual: EventEmitter<any> = new EventEmitter();
  @Output() book: EventEmitter<any> = new EventEmitter<any>();
  @Output() checkAllAvailabilitiesSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() nextAvailability: EventEmitter<any> = new EventEmitter<any>();
  @Output() bookMultiple: EventEmitter<any> = new EventEmitter<any>();
  @Output() showMaxReservationTimeModalEventEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() showExceedingKmLimitModalEventEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() sendNotificationChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public images = images;
  public moment = moment;
  public TooltipPosition = TooltipPosition;


  public interval: any;
  public timeCounter = 0;

  public availabilitiesArray: (
    { date: string; type: 'success'; availability: IAvailabilitySuccess[] } |
    { date: string; type: 'failure'; availability: IAvailabilityFailure[] }
  )[] = [];

  constructor(
    public literalService: LiteralService,
    public bookingsService: BookingsService,
    public userService: UsersService,
    public utilsService: UtilsService
  ) {

  }

  ngOnInit(): void {
    this.startTimer();
    for (const date in this.availabilities) {
      if (this.availabilities.hasOwnProperty(date)) {
        this.availabilitiesArray.push(...this.availabilities[date].success.map((trip: IAvailabilitySuccess[]) => ({ date, type: 'success', availability: trip })));
        this.availabilitiesArray.push(...this.availabilities[date].failure.map((availability: IAvailabilityFailure[]) => ({ date, type: 'failure', availability })));
      }
    }
    this.availabilitiesArray.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); 
  }

  startTimer() {
    this.interval = setInterval(() => {
      this.timeCounter++;
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availabilities']) this.availabilities = changes['availabilities'].currentValue;
  }

  selectAvailability(trip: IAvailabilitySuccess[]) {
    this.nextAvailability.emit(trip);
  }

  checkAvailabilitySelected(trip: IAvailabilitySuccess[]) {
    return this.availabilitiesSelected.find((selectedTrip: any) => selectedTrip[0].serviceAvailabilityResponseId === trip[0].serviceAvailabilityResponseId);
  }

  hasSeveralLegs(trip: IAvailability[]) {
    return trip.length > 1;
  }

  isTripInformative(trip: IAvailabilitySuccess[]) {
    return trip.some((leg) => leg.informative);
  }

  showAvailabilityDetail(trip: IAvailabilitySuccess[] | IAvailabilityFailure[]) {
    return hasAvailabilityDetail(this.serviceInfos, trip[0].serviceInfo, trip[0].alternative);
  }

  hasEcoRoute(trip: IAvailabilitySuccess[]) {
    return trip.some((leg) => leg.ecoRoute);
  }

  isIReservationAvailability = (obj: any): obj is IReservationAvailability => 'optimal' in obj;

  async checkExceptions(eventEmitter: EventEmitter<any>, multiple: boolean = false) {
    let exceedsKmLimit = false;
    let maxReservationTime = false;

    const trip: IAvailabilitySuccess[] = multiple ? this.getFirstSuccess(this.availabilities) : this.availabilitiesSelected[0];
    maxReservationTime = trip.some((leg) => leg.maxBookingTime > 0 && this.timeCounter >= leg.maxBookingTime);

    const nBookings = Object.keys(this.availabilities).filter((availability) => this.availabilities[availability].success.length > 0).length;
    const tripSuccess = this.getFirstSuccess(this.availabilities)!;
    // km limit is measured over the whole trip: first leg's pickup to last leg's drop-off, not per leg.
    exceedsKmLimit = await this.bookingsService.isExceedingKmLimit(
      this.newBooking.targetUserId,
      tripSuccess[0].inStop.id,
      tripSuccess[tripSuccess.length - 1].outStop.id,
      tripSuccess[0].serviceId,
      nBookings
    );

    if (maxReservationTime) {
      this.showMaxReservationTimeModalEventEmitter.emit();
    } else if (exceedsKmLimit) {
      this.showExceedingKmLimitModalEventEmitter.emit();
    } else {
      eventEmitter.emit(multiple);
      this.availabilitiesSelected = [];
      this.availabilities = null;
    }
  }

  getFirstSuccess(data: any): IAvailabilitySuccess[] | null {
    for (const dateKey in data) {
      const dateGroup = data[dateKey];
      if (dateGroup.success && dateGroup.success.length > 0) {
        return dateGroup.success[0];
      }
    }
    return null;
  }
  
  async makeBooking() {
    await this.checkExceptions(this.book);
  }
  
  async multipleBooking() {
    await this.checkExceptions(this.bookMultiple, true);
  }

  checkIfAlternativeIsOnInitial(availability: IAvailability) {
    return this.bookingsService.isAlternativeOnInitial(availability, this.newBooking);
  }

  checkIfAlternativeIsOnFinal(availability: IAvailability) {
    return this.bookingsService.isAlternativeOnFinal(availability, this.newBooking);
  }

  getAlternativeLeg(trip: IAvailability[]) {
    const firstLeg = trip[0];
    const lastLeg = trip[trip.length - 1];
    if (firstLeg.alternative && this.checkIfAlternativeIsOnInitial(firstLeg)) {
      return firstLeg;
    }
    if (lastLeg.alternative && this.checkIfAlternativeIsOnFinal(lastLeg)) {
      return lastLeg;
    }
    return undefined;
  }

  getAlterationDestinationName(availability: IAvailability, initial: boolean = true) {
    if (this.checkIfAlternativeIsOnInitial(availability) && initial) {
      return this.newBooking.originStop.name;
    }
    if (this.checkIfAlternativeIsOnFinal(availability) && !initial) {
      return this.newBooking.destinationStop.name;
    }
    return null;
  }

  hasAlterations(serviceInfoId: number) {
    const serviceInfo = this.serviceInfos.find((serviceInfo: IServiceInfo) => serviceInfo.id === serviceInfoId);
    return serviceInfo ? serviceInfo.alterationMessages.length > 0 : false;
  }

  getAlternativeMessage(serviceInfoId: number) {
    const textKey = this.hasAlterations(serviceInfoId) ? 'bookings.alternative.textAlteration' : 'bookings.alternative.text';
    return `<strong>${this.literalService.get('bookings.alternative.title', true)}</strong></br>${this.literalService.get(textKey, true)}`;
  }

  getAlterationMessageByService(serviceInfoId: number) {
    let messages = `<strong>${this.literalService.get('bookings.alteration', true)}:</strong></br>`;
    const serviceInfo = this.serviceInfos.find((serviceInfo: IServiceInfo) => serviceInfo.id === serviceInfoId);
    serviceInfo && serviceInfo.alterationMessages.forEach((message: string) => {
      messages += message;
    });
    return messages;
  }

  getVariabilityText(availabilityInOutStop: any) {
    if (availabilityInOutStop.minDateTime === availabilityInOutStop.maxDateTime) {
      return this.literalService.get('bookings.tooltips.withoutVariability', true);
    }
    return this.literalService.get('bookings.tooltips.variability', true) + ' <strong>' + moment(availabilityInOutStop.minDateTime).format('HH:mm') + ' - ' + moment(availabilityInOutStop.maxDateTime).format('HH:mm') + '</strong>';
  }

  isFirstInSpecificDate(index: number, date: string): boolean {
    return index === this.availabilitiesArray.findIndex(item => item.date === date);
  }

  hasSuccessAvailabilities() {
    const dates = Object.keys(this.availabilities);
    const result = dates.some((date: any) => this.availabilities[date].success.length > 0)
    return result;
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

}
